'use client';

import { useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { SvgSpinner } from '@icons/oldIcons/SvgSpinner';
import { Product, ProductType } from '@interface/product';
import { HOLAGLOW_COLORS } from '@utils/colors';
import { setSeoMetaData } from '@utils/common';
import { filterItems } from '@utils/filterItems';
import { getDiscountedPrice } from '@utils/productCommon';
import { ProductCardData } from '@utils/productDto';
import AnimateOnViewport from 'app/(web)/components/common/AnimateOnViewport';
import CategorySelector from 'app/(web)/components/filters/CategorySelector';
import ProductCard from 'app/(web)/components/product/ProductCard';
import ProductSearchBar from 'app/(web)/components/product/ProductSearchBar';
import {
  ApplicationOrigin,
  useGlobalStore,
  useSessionStore,
} from 'app/stores/globalStore';
import { Container, Flex } from 'designSystem/Layouts/Layouts';
import { Text } from 'designSystem/Texts/Texts';
import { isEmpty } from 'lodash';

import { applyFilters, INITIAL_FILTERS } from '../utils/filters';
import PVBanner from './PVBanner';

/**
 * Categoría de filtro que corresponde a un hub (/tratamientos/relleno,
 * /piel, /pelo…), o '' si no existe en `filterItems`.
 */
function hubCategory(slug: string): string {
  if (!slug) return '';
  const value =
    slug === 'piel'
      ? 'Calidad de la Piel'
      : slug === 'pelo'
        ? 'Caida del pelo'
        : slug[0].toUpperCase() + slug.substr(1).toLowerCase();
  const exists = filterItems.some(x =>
    x.buttons.some(button => button.value == value)
  );
  if (!exists) return '';
  return value === 'Calidad de la Piel' ? 'Calidad Piel' : value;
}

export default function PsrpProductList({
  products,
  slug = '',
  type,
  intro = '',
}: {
  products: ProductCardData[];
  slug?: string;
  type: ProductType;
  /** Texto descriptivo de la categoría; se pinta al final de la lista. */
  intro?: string;
}) {
  const {
    filteredProducts,
    setFilteredProducts,
    productFilters,
    setProductFilters,
  } = useGlobalStore(state => state);
  const { currentOrigin, selectedClinic } = useSessionStore(state => state);

  const [isHydrated, setIsHydrated] = useState(true);

  // En servidor (y en el primer render del cliente) el store está vacío y la
  // lista solo se pintaba tras hidratar: el HTML de las categorías no llevaba
  // ningún producto. Ahora la lista inicial se calcula aquí con los mismos
  // filtros (tipo + categoría del hub) y, cuando los efectos rellenan el
  // store, se usa `filteredProducts` como hasta ahora.
  const [listReady, setListReady] = useState(false);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  // react-device-detect no coincide entre servidor y cliente: solo tras montar.
  const isMobileClient = mounted && isMobile;
  const initialProducts = useMemo(() => {
    const category = hubCategory(slug);
    return applyFilters({
      products,
      filters: {
        ...INITIAL_FILTERS,
        type: [type],
        category: category ? [category] : [],
      },
    });
  }, [products, slug, type]);
  // La sesión (clínica elegida, origen) viene de sessionStorage y no existe
  // en servidor: hasta montar se ordena sin descuentos por clínica.
  const sortOrigin = mounted
    ? currentOrigin || ApplicationOrigin.Web
    : ApplicationOrigin.Web;
  const sortClinicId = mounted ? selectedClinic?.id : undefined;

  const metadataPacks = {
    title: 'Packs de tratamientos de medicina estética - Holaglow',
    description:
      'Elige uno de los packs para tratar de manera global tus objetivos estéticos y conseguir el resultado que deseas',
  };

  useEffect(() => {
    if (slug !== '') {
      const filterToApply = hubCategory(slug);
      if (
        filterToApply &&
        productFilters.category.indexOf(filterToApply) == -1
      ) {
        productFilters.category.push(filterToApply);
      }

      if (slug === 'packs') {
        setSeoMetaData(metadataPacks.title, metadataPacks.description);
      }

      setProductFilters(productFilters);
      processFilters();
    }

    if (type) {
      setProductFilters({
        ...productFilters,
        type: [type],
      });
    }
  }, [slug, type, products]);

  useEffect(() => {
    processFilters();
  }, [productFilters]);

  useEffect(() => {
    if (filteredProducts.length > 0) {
      setIsHydrated(true);
    }
  }, [filteredProducts]);

  useEffect(() => {
    if (isHydrated && productFilters.type.length > 0) {
      processFilters();
    }
  }, [isHydrated]);

  function processFilters() {
    if (isEmpty(filteredProducts)) {
      // El store sigue tipado como Product[]; las tarjetas solo necesitan
      // los campos de ProductCardData.
      setFilteredProducts(products as unknown as Product[]);
      setFilteredProducts(
        applyFilters({ products, filters: productFilters }) as unknown as Product[]
      );
    } else {
      setFilteredProducts(
        applyFilters({ products: filteredProducts, filters: productFilters })
      );
    }
    setListReady(true);
  }

  function sortProductsByPriority<T extends ProductCardData>(products: T[]): T[] {
    return [...products].sort((a, b) => {
      const aHasDiscount =
        getDiscountedPrice(
          a,
          sortOrigin,
          sortClinicId
        ) !== null;
      const bHasDiscount =
        getDiscountedPrice(
          b,
          sortOrigin,
          sortClinicId
        ) !== null;

      const aIsNew = (() => {
        const productWithDate = a as T & { creationDate?: Date | string };
        if (!productWithDate.creationDate) return false;
        const creationDate = new Date(productWithDate.creationDate);
        const now = new Date();
        if (creationDate > now) return false;
        const diffTime = now.getTime() - creationDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays < 7;
      })();

      const bIsNew = (() => {
        const productWithDate = b as T & { creationDate?: Date | string };
        if (!productWithDate.creationDate) return false;
        const creationDate = new Date(productWithDate.creationDate);
        const now = new Date();
        if (creationDate > now) return false;
        const diffTime = now.getTime() - creationDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays < 7;
      })();

      if (aHasDiscount && !bHasDiscount) return -1;
      if (!aHasDiscount && bHasDiscount) return 1;
      if (aIsNew && !bIsNew) return -1;
      if (!aIsNew && bIsNew) return 1;
      return 0;
    });
  }

  if (!isHydrated && filteredProducts.length > 0) {
    return <></>;
  }

  const list: ProductCardData[] = listReady ? filteredProducts : initialProducts;

  return (
    <>
      {isEmpty(list) && (
        <Flex layout="row-left" className="justify-center">
          <SvgSpinner
            fill={HOLAGLOW_COLORS['secondary']}
            height={50}
            width={50}
          />
        </Flex>
      )}

      {!isEmpty(list) && (
        <div className="bg-hg-cream300 pb-32 relative">
          <Container className="md:hidden pt-4">
            {isMobileClient && (
              <ProductSearchBar
                products={products}
                className="mb-4"
                isMobileNavigation={isMobileClient}
              />
            )}
          </Container>
          <Container className="px-0 md:px-4 md:pt-8 mb-8 relative">
            <div className="md:flex md:flex-row md:justify-start items-center">
              {!isMobileClient && (
                <ProductSearchBar
                  products={products}
                  isMobileNavigation={isMobileClient}
                  className="hidden md:block mr-4 md:min-w-[280px] md:max-w-[320px] md:mb-4 lg:mb-0 shrink-0"
                />
              )}
              <AnimateOnViewport
                origin={isMobileClient ? 'right' : 'bottom'}
                className="mb-4 md:mb-0 min-w-0"
              >
                <CategorySelector type={type} products={products} />
              </AnimateOnViewport>
            </div>
          </Container>

          <Container className="px-8 md:px-4">
            <ul className="transition-all grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 flex-col gap-8 md:gap-6 md:pt-0 pb-6">
              <li key="pvBanner">
                <PVBanner
                  type={
                    type === ProductType.Medical
                      ? 'pv'
                      : type === ProductType.Esthetic
                        ? 'asesoramientoPiel'
                        : type === ProductType.Longevity
                          ? 'asesoramientoLongevidad'
                          : type === ProductType.Surgery
                            ? 'surgery'
                            : 'pv'
                  }
                />
              </li>

              {sortProductsByPriority(list)
                .filter(product => product.visibility)
                .map(product => {
                  return (
                    <li key={product.id}>
                      <ProductCard
                        product={product}
                        className="h-full flex flex-col"
                        showUpgradeText={type === ProductType.Surgery}
                      />
                    </li>
                  );
                })}
            </ul>
            {/* Texto descriptivo de la categoría: al final de la lista y
                discreto, no bajo el título. */}
            {intro && (
              <Text size="sm" className="text-hg-black500 max-w-3xl mt-8">
                {intro}
              </Text>
            )}
          </Container>
        </div>
      )}
    </>
  );
}
